import { useEffect, useRef } from "preact/hooks";
import { useBackdrop } from "~/hooks";
import { parseParamsStr } from "~/utils";
import { UseItemSetupParams } from "./interfaces";

const useItemSetup = ({
    ref,
    isActiveRecord,
    isInHistory,
    onClose,
}: UseItemSetupParams) => {
    const portalRef = useRef<HTMLDivElement | null>(null);
    const placeholderRef = useRef<HTMLDivElement | null>(null);
    const targetRef = useRef<HTMLElement | null>(null);

    useBackdrop({
        noTarget: true,
        skip: !isInHistory,
        blur: "sm",
        unmountTimeout: 50, // to prevent flickering when navigating between pages
    });

    useEffect(() => {
        const target = ref?.current?.closest<HTMLElement>(
            "[data-discovery-target]",
        );

        targetRef.current = target ?? null;

        if (!target) {
            return;
        }

        const config = parseParamsStr(target.dataset.discoveryTarget);
        const variant = config.v ?? "default";
        let portal = portalRef.current;
        let placeholder = placeholderRef.current;

        if (isActiveRecord) {
            const rect = target.getBoundingClientRect();
            portal = portal ?? document.createElement("div");
            placeholder = placeholder ?? document.createElement("div");

            portalRef.current = portal;
            placeholderRef.current = placeholder;

            placeholder.style.width = `${rect.width}px`;
            placeholder.style.height = `${rect.height}px`;

            portal.setAttribute("aria-hidden", "false");
            portal.classList.add("dtp", `dtp-${variant}`);
            portal.style.position = "fixed";
            portal.style.top = `${rect.top}px`;
            portal.style.left = `${rect.left}px`;
            portal.style.width = `${rect.width}px`;
            portal.style.height = `${rect.height}px`;
            portal.style.zIndex = "101";
            portal.style.opacity = "0";
            portal.style.transition = "opacity 100ms";

            target.replaceWith(placeholder);
            portal.appendChild(target);

            document.body.appendChild(portal);

            portal.getBoundingClientRect();
            portal.style.opacity = "1";
        } else if (portal) {
            portal.style.opacity = "0";
            portal.addEventListener("transitionend", () => {
                if (portal && placeholder) {
                    portal.remove();
                    placeholder.replaceWith(target);
                    portalRef.current = null;
                    placeholderRef.current = null;
                    onClose?.();
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActiveRecord]);

    useEffect(() => {
        return () => {
            const portal = portalRef.current;
            const placeholder = placeholderRef.current;
            const target = targetRef.current;

            onClose?.();

            if (portal) {
                portal.remove();
            }

            if (placeholder && target) {
                placeholder.replaceWith(target);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};

export default useItemSetup;
